import { For, Show } from "solid-js";
import type { VoyageDetails } from "../../shared/api/types";
import { formatNumber } from "../../shared/locale";
import { fmtLocal } from "./details-format";
import s from "./StatusSection.module.css";

export interface StatusSectionProps {
  details: VoyageDetails;
  // Resolved adjacent-voyage details, fetched by the voyage-details store
  // and passed in so this section can render the clickable voyageReference
  // link instead of just the bare voyage number. null while loading;
  // "no-ref" string if the fetch failed (mirrors OLD `(no ref)` fallback).
  prevAdjacent: VoyageDetails | null | "loading" | "no-ref";
  nextAdjacent: VoyageDetails | null | "loading" | "no-ref";
  // Called when the user clicks a Prev/Next ref link; jumps to the Vessels
  // tab and selects the target voyage.
  onVoyageRefClick: (vesselCode: string, voyageNo: string) => void;
}

export function StatusSection(props: StatusSectionProps) {
  const fields = () => {
    const d = props.details;
    const out: { k: string; v: string }[] = [];
    out.push({ k: "Status", v: d.voyageStatus || "—" });
    out.push({ k: "Route", v: d.tradeArea || "—" });
    out.push({ k: "Trade", v: d.lob || "—" });
    out.push({ k: "Sea days", v: d.totalSeaDays != null ? d.totalSeaDays.toFixed(1) : "—" });
    out.push({ k: "Port days", v: d.totalPortDays != null ? d.totalPortDays.toFixed(1) : "—" });
    out.push({
      k: "Distance (nm)",
      v: d.endOfVoyageDistance != null ? formatNumber(Math.round(d.endOfVoyageDistance)) : "—",
    });
    out.push({ k: "Commenced", v: fmtLocal(d.commenceDateLocal) });
    out.push({ k: "Completes", v: fmtLocal(d.completeDateLocal) });
    if (d.opsCoordinator) out.push({ k: "Ops coord.", v: d.opsCoordinator });
    return out;
  };

  function renderAdjacent(
    voyageNo: string | null | undefined,
    adj: StatusSectionProps["prevAdjacent"],
  ) {
    if (voyageNo == null) return null;
    if (adj === "loading" || adj === null) {
      return (
        <>
          <span class="muted">#{voyageNo} </span>
          <span class={s.adjLoading}>…</span>
        </>
      );
    }
    if (adj === "no-ref") {
      return <span class="muted">#{voyageNo} (no ref)</span>;
    }
    return (
      <>
        <a
          href="#"
          class={s.voyageRefLink}
          onClick={(e) => {
            e.preventDefault();
            props.onVoyageRefClick(props.details.vesselCode, adj.voyageNo);
          }}
        >
          {adj.voyageReference}
        </a>{" "}
        <span class="muted">#{adj.voyageNo}</span>
      </>
    );
  }

  return (
    <>
      <dl class="details-list">
        <For each={fields()}>
          {(f) => (
            <>
              <dt>{f.k}</dt>
              <dd>{f.v}</dd>
            </>
          )}
        </For>
        <Show when={props.details.previousVoyageNo}>
          <dt>Prev voyage</dt>
          <dd>{renderAdjacent(props.details.previousVoyageNo, props.prevAdjacent)}</dd>
        </Show>
        <Show when={props.details.nextVoyageNo}>
          <dt>Next voyage</dt>
          <dd>{renderAdjacent(props.details.nextVoyageNo, props.nextAdjacent)}</dd>
        </Show>
      </dl>
    </>
  );
}
